import React, { useState, useEffect } from 'react';
import { Modal, toast } from '../ui.jsx';
import { getApiBase } from '../apiBase';

const API_BASE = getApiBase();
// ── NoticeBoard ───────────────────────────────────────────────────
const NOTICE_TAGS = ['General', 'Event', 'Important', 'Holiday', 'Admission'];
const TAG_COLORS = { Event: '#1d4ed8', Important: '#dc2626', Holiday: '#059669', Admission: '#7c3aed', General: '#475569' };
const TAG_BG = { Event: '#dbeafe', Important: '#fee2e2', Holiday: '#d1fae5', Admission: '#ede9fe', General: '#f1f5f9' };

function NoticeBoard() {
  const [notices, setNotices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [modal, setModal] = React.useState(null); // null | { mode:'add'|'edit', data }
  const [form, setForm] = React.useState({ title: '', body: '', tag: 'General', pinned: false, active: true });
  const [saving, setSaving] = React.useState(false);
  const API = `${API_BASE}/notices`;
  const token = () => sessionStorage.getItem('lkps_token') || '';

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(API, { headers: { Authorization: 'Bearer ' + token() } });
      const data = await r.json();
      setNotices(Array.isArray(data) ? data : []);
    } catch { toast('Failed to load notices', 'err'); }
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm({ title: '', body: '', tag: 'General', pinned: false, active: true });
    setModal({ mode: 'add' });
  };

  const openEdit = (n) => {
    setForm({ title: n.title, body: n.body || '', tag: n.tag, pinned: n.pinned, active: n.active });
    setModal({ mode: 'edit', id: n._id });
  };

  const saveNotice = async () => {
    if (!form.title.trim()) return toast('Title is required', 'err');
    if (!modal) return;
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        const r = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() }, body: JSON.stringify(form) });
        const n = await r.json();
        if (!r.ok) { toast(n.error || 'Failed to publish', 'err'); setSaving(false); return; }
        setNotices(prev => [n, ...prev]);
        toast('Notice published');
      } else {
        const r = await fetch(`${API}/${modal.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() }, body: JSON.stringify(form) });
        const n = await r.json();
        if (!r.ok) { toast(n.error || 'Failed to update', 'err'); setSaving(false); return; }
        setNotices(prev => prev.map(x => x._id === modal.id ? n : x));
        toast('Notice updated');
      }
      setModal(null);
    } catch (e) { toast('Save failed: ' + e.message, 'err'); }
    setSaving(false);
  };

  const toggleActive = async (n) => {
    try {
      const r = await fetch(`${API}/${n._id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() }, body: JSON.stringify({ active: !n.active }) });
      const updated = await r.json();
      setNotices(prev => prev.map(x => x._id === n._id ? updated : x));
    } catch { toast('Update failed', 'err'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this notice?')) return;
    try {
      await fetch(`${API}/${id}`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token() } });
      setNotices(prev => prev.filter(x => x._id !== id));
      toast('Deleted');
    } catch { toast('Delete failed', 'err'); }
  };

  const active = notices.filter(n => n.active).length;
  const pinned = notices.filter(n => n.pinned).length;

  return (
    <div>
      {/* Summary */}
      <div className="notices-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Notices', value: notices.length, color: '#1960a3', icon: 'campaign' },
          { label: 'Active (Visible)', value: active, color: '#059669', icon: 'visibility' },
          { label: 'Pinned', value: pinned, color: '#f59e0b', icon: 'push_pin' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: c.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22, color: c.color }}>{c.icon}</span>
            </div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 900, color: c.color, fontFamily: 'Manrope,sans-serif', lineHeight: 1 }}>{c.value}</div>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginTop: 3 }}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Notice Board ({notices.length})</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>refresh</span> Refresh
            </button>
            <button className="btn btn-primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 15 }}>add</span> New Notice
            </button>
          </div>
        </div>
        <div className="table-wrap">
          {loading ? (
            <div className="no-data"><span className="material-symbols-outlined no-data-icon">hourglass_top</span>Loading…</div>
          ) : notices.length === 0 ? (
            <div className="no-data"><span className="material-symbols-outlined no-data-icon">campaign</span>No notices yet. Create one!</div>
          ) : (
            <>
              <table className="notices-desktop-table">
                <thead>
                  <tr>
                    <th>Title</th><th>Tag</th><th>Body</th><th>Pinned</th><th>Status</th><th>Date</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notices.map(n => (
                    <tr key={n._id}>
                      <td style={{ fontWeight: 700, maxWidth: 220 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {n.pinned && <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#f59e0b' }}>push_pin</span>}
                          {n.title}
                        </div>
                      </td>
                      <td>
                        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', background: TAG_BG[n.tag] || '#f1f5f9', color: TAG_COLORS[n.tag] || '#475569' }}>{n.tag}</span>
                      </td>
                      <td style={{ color: '#64748b', fontSize: 12, maxWidth: 200 }}>
                        {n.body ? (n.body.length > 60 ? n.body.slice(0, 60) + '…' : n.body) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </td>
                      <td>
                        <button onClick={() => { fetch(`${API}/${n._id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() }, body: JSON.stringify({ pinned: !n.pinned }) }).then(r => r.json()).then(u => setNotices(p => p.map(x => x._id === n._id ? u : x))); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 18, color: n.pinned ? '#f59e0b' : '#cbd5e1' }}>push_pin</span>
                        </button>
                      </td>
                      <td>
                        <button onClick={() => toggleActive(n)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 20, border: 'none', fontWeight: 700, fontSize: 11, cursor: 'pointer', background: n.active ? '#d1fae5' : '#f1f5f9', color: n.active ? '#059669' : '#94a3b8' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{n.active ? 'visibility' : 'visibility_off'}</span>
                          {n.active ? 'Active' : 'Hidden'}
                        </button>
                      </td>
                      <td style={{ color: '#94a3b8', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {new Date(n.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm" onClick={() => openEdit(n)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>edit</span>
                          </button>
                          <button className="btn btn-sm btn-danger" onClick={() => del(n._id)} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="notices-mobile-list">
                {notices.map(n => (
                  <div key={n._id} className="notices-mobile-card">
                    <div className="notices-mobile-head">
                      <div>
                        <div className="notices-mobile-title">
                          {n.pinned && <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#f59e0b' }}>push_pin</span>}
                          {n.title}
                        </div>
                        <div className="notices-mobile-meta">
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, background: TAG_BG[n.tag] || '#f1f5f9', color: TAG_COLORS[n.tag] || '#475569', fontSize: 10, fontWeight: 700 }}>{n.tag}</span>
                          <span style={{ padding: '2px 8px', borderRadius: 20, background: n.active ? '#d1fae5' : '#f1f5f9', color: n.active ? '#059669' : '#94a3b8', fontSize: 10, fontWeight: 700 }}>{n.active ? 'Active' : 'Hidden'}</span>
                        </div>
                      </div>
                      <div className="notices-mobile-date">{new Date(n.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                    </div>
                    <div className="notices-mobile-body">{n.body || 'No description added.'}</div>
                    <div className="notices-mobile-actions">
                      <button className="btn" onClick={() => openEdit(n)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                        Edit
                      </button>
                      <button className="btn" onClick={() => { fetch(`${API}/${n._id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token() }, body: JSON.stringify({ pinned: !n.pinned }) }).then(r => r.json()).then(u => setNotices(p => p.map(x => x._id === n._id ? u : x))); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>push_pin</span>
                        {n.pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button className="btn" onClick={() => toggleActive(n)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{n.active ? 'visibility_off' : 'visibility'}</span>
                        {n.active ? 'Hide' : 'Show'}
                      </button>
                      <button className="btn btn-danger" onClick={() => del(n._id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{modal.mode === 'add' ? 'New Notice' : 'Edit Notice'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label>Title *</label>
                <input className="form-control" placeholder="Notice title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Body / Description</label>
                <textarea className="form-control" placeholder="Optional details…" rows={3} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
              <div className="notices-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label>Tag</label>
                  <select className="form-control" value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}>
                    {NOTICE_TAGS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                  <label>Options</label>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', paddingTop: 6 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155', textTransform: 'none', letterSpacing: 'normal' }}>
                      <input type="checkbox" checked={form.pinned} onChange={e => setForm(f => ({ ...f, pinned: e.target.checked }))} /> Pinned
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155', textTransform: 'none', letterSpacing: 'normal' }}>
                      <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Active
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveNotice} disabled={saving}>
                {saving ? 'Saving…' : modal.mode === 'add' ? 'Publish Notice' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default NoticeBoard;
